import {
  createContext,
  type ReactNode,
  startTransition,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  personalizationAttributesSchema,
  type PersonalizationAttributes,
} from './component-personalization.schema';
import { logger } from '@sentry/gatsby';
import errorLogger from '../errorLogger';

interface ComponentPersonalizationContextType {
  attributes: PersonalizationAttributes | undefined;
}

const ComponentPersonalizationContext = createContext<ComponentPersonalizationContextType>(
  null as unknown as ComponentPersonalizationContextType
);

export const useComponentPersonalizationContext = () => {
  const contextValues = useContext(ComponentPersonalizationContext);
  if (contextValues === null) {
    throw new Error(
      'useComponentPersonalizationContext must be used within a ComponentPersonalizationProvider'
    );
  }

  return contextValues;
};

interface ComponentPersonalizationProviderProps {
  children?: ReactNode;
}
const ComponentPersonalizationProvider = ({ children }: ComponentPersonalizationProviderProps) => {
  const loadingAttempsRef = useRef(0);
  const [personalizationAttributes, setPersonalizationAttributes] =
    useState<PersonalizationAttributes>();
  const loadingStartTimeRef = useRef(Date.now());

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (typeof personalizationAttributes !== 'undefined') {
      return;
    }

    if (loadingAttempsRef.current >= 10) {
      return;
    }

    const interval = setInterval(() => {
      if (loadingAttempsRef.current >= 10) {
        logger.info('Component Personalization - Timed out after 10 retries');
        clearInterval(interval);
        return;
      }

      loadingAttempsRef.current++;

      const personalizationJSON = localStorage.getItem('_6senseCompanyDetails');

      if (typeof personalizationJSON !== 'string') {
        return;
      }

      startTransition(() => {
        try {
          const personalizationData = JSON.parse(personalizationJSON);
          setPersonalizationAttributes(personalizationAttributesSchema.parse(personalizationData));
          clearInterval(interval);

          const endTime = Date.now();
          const difference = endTime - loadingStartTimeRef.current;

          logger.info('Component Personalization - Initialized', {
            duration: `${difference}ms`,
            attributes: personalizationData,
          });
        } catch (error) {
          errorLogger(error, {
            tags: {
              type: 'component-personalization',
            },
          });
        }
      });
    }, 200);

    return () => {
      clearInterval(interval);
    };
  }, [personalizationAttributes]);

  return (
    <ComponentPersonalizationContext.Provider
      value={useMemo(
        () => ({
          attributes: personalizationAttributes,
        }),
        [personalizationAttributes]
      )}
    >
      {children}
    </ComponentPersonalizationContext.Provider>
  );
};

export default ComponentPersonalizationProvider;
